import { Application, Container } from 'pixi.js';

import { CurrentPoint } from 'ff/debrief/models';
import { HudContext } from 'ff/debrief/components/hud/HudContext';
import { HudPositionTranslator } from 'ff/debrief/components/hud/utils';

/**
 * Position specification for a widget on the HUD (in pixels)
 * These values are computed from percentages by HudRenderer
 */
export type WidgetPosition = {
    x: number; // X position in pixels
    y: number; // Y position in pixels
    width: number; // Width in pixels
    height: number; // Height in pixels
    anchor?: { x: number; y: number }; // Anchor point (default not specified)
};

/**
 * Abstract base class for all HUD widgets.
 *
 * Provides lifecycle management, data dependency declaration, position translation,
 * and configuration support for HUD display elements.
 *
 * @template TConfig - Type of widget-specific configuration. Use Record<string, never> for widgets without config.
 *
 * @example
 * ```typescript
 * // Widget without config
 * class SimpleWidget extends BaseWidget {
 *   static readonly requiredProperties = ['latitude'] as const;
 *   // ...
 * }
 *
 * // Widget with typed config
 * interface AltitudeConfig {
 *   minAltitude: number;
 *   maxAltitude: number;
 * }
 *
 * class AltitudeWidget extends BaseWidget<AltitudeConfig> {
 *   static readonly requiredProperties = ['altitudeFeet'] as const;
 *
 *   protected updateWidget(point, hudContext, positionTranslator) {
 *     const min = this.config?.minAltitude ?? 0;
 *     // ...
 *   }
 * }
 * ```
 */
export abstract class BaseWidget<TConfig = Record<string, never>> {
    /**
     * Position translator for converting percentages to pixels within widget coordinate space.
     * Scoped to the widget's dimensions for responsive internal layout.
     */
    protected positionTranslator: HudPositionTranslator;

    /**
     * Widget-specific configuration passed during initialization.
     * Access via this.config in subclasses with appropriate type safety.
     */
    protected config?: TConfig;

    /**
     * Shared HUD context containing global configuration and utilities.
     * Stored during initialization for use in populate() and update().
     */
    private hudContext: HudContext;

    /**
     * PixiJS container holding all visual elements for this widget.
     * Created during init() and populated in populate().
     */
    protected elementContainer: Container;

    /**
     * Data properties required for this widget to display.
     * Override in subclasses to declare dependencies.
     * Widget will not be initialized if required properties are unavailable.
     */
    static readonly requiredProperties: readonly (keyof CurrentPoint)[] = [];

    /**
     * Optional data properties that enhance widget display but are not required.
     * Override in subclasses to declare optional dependencies.
     */
    static readonly optionalProperties: readonly (keyof CurrentPoint)[] = [];

    /**
     * Determines if this widget should be displayed based on available data properties.
     *
     * Default implementation checks if all required properties are available.
     * Subclasses can override to provide custom logic (e.g., critical widgets that always display).
     *
     * @param availableProperties - Set of data properties available from the current data source
     * @returns True if widget should be displayed, false otherwise
     *
     * @example
     * ```typescript
     * // Custom logic: always show airspeed widget, even if data unavailable (show INOP)
     * class AirspeedWidget extends BaseWidget {
     *   static readonly requiredProperties = ['indicatedAirSpeed'] as const;
     *
     *   static willDisplay(availableProperties: Set<keyof CurrentPoint>): boolean {
     *     return true; // Always display
     *   }
     * }
     * ```
     */
    static willDisplay(availableProperties: Set<keyof CurrentPoint>): boolean {
        // Default: check if all required properties are available
        return this.requiredProperties.every((prop: keyof CurrentPoint) =>
            availableProperties.has(prop)
        );
    }

    /**
     * Initialize the widget container and positioning.
     *
     * Creates a PixiJS container, applies positioning and anchoring, sets up the position translator,
     * and adds the container to the parent. Call populate() after init() to create visual elements.
     *
     * @param pixiApp - The PixiJS application instance
     * @param hudContext - Shared HUD context containing global config and utilities
     * @param position - Widget position and dimensions in pixels (converted from percentages by HudRenderer)
     * @param config - Optional widget-specific configuration
     * @param parentContainer - Optional parent container (defaults to pixiApp.stage)
     * @returns Promise that resolves when initialization is complete
     */
    async init(
        pixiApp: Application,
        hudContext: HudContext,
        position: WidgetPosition,
        config?: TConfig,
        parentContainer?: Container
    ): Promise<void> {
        this.hudContext = hudContext;
        // Store widget-specific configuration
        this.config = config;

        // Apply position (already in pixels)
        this.elementContainer = new Container({
            position: { x: position.x, y: position.y },
            height: position.height,
            width: position.width,
        });

        // Apply anchor if specified
        if (position.anchor) {
            this.elementContainer.pivot.set(
                position.width * position.anchor.x,
                position.height * position.anchor.y
            );
        }

        // Create and store a position translator for this widget's coordinate space
        this.positionTranslator = new HudPositionTranslator({
            width: position.width,
            height: position.height,
        });

        const addToContainer = parentContainer ?? pixiApp.stage;
        addToContainer.addChild(this.elementContainer);
    }

    /**
     * Populate the widget with visual elements.
     *
     * Called after init() to create and add the widget's visual elements to the container.
     * Delegates to populateElementContainer() for widget-specific implementation.
     *
     * @returns Promise that resolves when population is complete
     */
    async populate() {
        return this.populateElementContainer(
            this.elementContainer,
            this.hudContext,
            this.positionTranslator
        );
    }

    /**
     * Destroy the widget and clean up all resources.
     * Delegates to cleanUp() for widget-specific cleanup.
     *
     * @returns Promise that resolves when cleanup is complete
     */
    async destroy() {
        this.cleanUp();
    }

    /**
     * Create and position the widget's visual elements within the provided container.
     *
     * Called by populate() after the widget container is created and positioned.
     * Implementations should:
     * - Create PixiJS display objects (Text, Graphics, Sprites, etc.)
     * - Use hudContext.globalConfig for visual properties
     * - Use positionTranslator for responsive internal layout
     * - Add all created objects to elementContainer
     *
     * @param elementContainer - The container to add visual elements to
     * @param hudContext - Shared HUD context with global config and utilities
     * @param positionTranslator - Position translator scoped to this widget's dimensions
     * @returns Promise that resolves when population is complete
     *
     * @example
     * ```typescript
     * async populateElementContainer(elementContainer, hudContext, positionTranslator) {
     *   const textStyle = {
     *     fontFamily: hudContext.globalConfig.fontFamily,
     *     fontSize: hudContext.globalConfig.baseFontSize,
     *     fill: hudContext.globalConfig.primaryLineColor
     *   };
     *
     *   this.text = new Text({ text: '', style: textStyle });
     *   this.text.position.set(10, 10);
     *   elementContainer.addChild(this.text);
     * }
     * ```
     */
    protected abstract populateElementContainer(
        elementContainer: Container,
        hudContext: HudContext,
        positionTranslator: HudPositionTranslator
    ): Promise<void>;

    /**
     * Update the widget with new flight data.
     *
     * Public method called by HudRenderer on each data update.
     * Delegates to updateWidget() via template method pattern.
     *
     * @param point - Current flight data point
     * @param hudContext - Shared HUD context
     */
    public update(point: CurrentPoint, hudContext: HudContext): void {
        this.updateWidget(point, hudContext, this.positionTranslator);
    }

    /**
     * Update the widget's visual elements with new flight data.
     *
     * Called by update() via template method pattern with positionTranslator provided.
     * Implementations should update text values, graphics, and visual properties.
     *
     * Data availability:
     * - Required properties are guaranteed to be present (checked by willDisplay())
     * - Optional properties should be checked via hudContext.availableProperties
     *
     * @param point - Current flight data point
     * @param hudContext - Shared HUD context with global config and utilities
     * @param positionTranslator - Position translator scoped to this widget's dimensions
     *
     * @example
     * ```typescript
     * protected updateWidget(point, hudContext, positionTranslator) {
     *   // Required property - guaranteed available
     *   this.altitudeText.text = `${Math.round(point.altitudeFeet)} ft`;
     *
     *   // Optional property - check availability
     *   if (hudContext.availableProperties.has('aboveGroundLevel')) {
     *     this.aglText.text = `AGL: ${Math.round(point.aboveGroundLevel)} ft`;
     *   }
     * }
     * ```
     */
    protected abstract updateWidget(
        point: CurrentPoint,
        hudContext: HudContext,
        positionTranslator: HudPositionTranslator
    ): void;

    /**
     * Clean up widget resources.
     *
     * Called during widget destruction. Implementations should:
     * - Destroy all PixiJS objects created in populateElementContainer
     * - Remove event listeners
     * - Clean up any other resources
     *
     * @example
     * ```typescript
     * cleanUp(): void {
     *   this.text?.destroy();
     *   this.graphics?.destroy();
     * }
     * ```
     */
    abstract cleanUp(): void;
}
